import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
	LayoutDashboard,
	User,
	Building2,
	ChevronUp,
	Check,
	Repeat,
} from "lucide-react";
import { useState, useRef, useCallback } from "react";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useHubProfile } from "@/hooks/hub/useHubProfile";
import { ROUTES } from "@/app/routes";
import { cn } from "@/utils";

interface NavItemConfig {
	path: string;
	icon: React.ReactNode;
	labelKey: string;
	fallback: string;
}

const navItems: NavItemConfig[] = [
	{
		path: ROUTES.HUB.DASHBOARD,
		icon: <LayoutDashboard size={20} />,
		labelKey: "hub.nav.dashboard",
		fallback: "Dashboard",
	},
	{
		path: ROUTES.HUB.PROFILE,
		icon: <User size={20} />,
		labelKey: "hub.nav.profile",
		fallback: "Perfil",
	},
];

/**
 * Profile Switcher Button for Bottom Nav
 * Only shown when user has multiple profiles
 */
const ProfileSwitcherButton = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const {
		profiles,
		selectedProfile,
		hasMultipleProfiles,
		isLoading,
		selectProfile,
	} = useHubProfile();

	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const closeDropdown = useCallback(() => setIsOpen(false), []);
	useClickOutside(dropdownRef, closeDropdown, isOpen);

	// Don't render if only one profile or loading
	if (!hasMultipleProfiles || isLoading || !selectedProfile) {
		return null;
	}

	const handleSelect = (uuid: string) => {
		if (uuid !== selectedProfile.uuid) {
			selectProfile(uuid);
			setIsOpen(false);
			// Navigate to dashboard to trigger data refetch with new profile
			navigate(ROUTES.HUB.DASHBOARD, { replace: true });
		} else {
			setIsOpen(false);
		}
	};

	const getTypeLabel = (type: string) => {
		return type === "company"
			? t("hub.profile_selector.company")
			: t("hub.profile_selector.individual");
	};

	return (
		<div
			ref={dropdownRef}
			className="relative flex flex-col items-center justify-center h-full px-4 py-1"
		>
			{/* Trigger button */}
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className={cn(
					"flex flex-col items-center justify-center transition-colors",
					isOpen
						? "text-text-primary"
						: "text-text-tertiary hover:text-text-secondary active:text-text-primary",
				)}
			>
				<span className="relative">
					<Repeat
						size={20}
						className={isOpen ? "text-text-primary" : "text-text-tertiary"}
					/>
					{/* Green dot indicator */}
					<span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-success-bar rounded-full border border-white"></span>
				</span>
				<span className="mt-1 text-2xs font-medium truncate flex items-center gap-0.5">
					{selectedProfile.display_name.split(" ")[0]}
					<ChevronUp
						size={10}
						className={cn("transition-transform", isOpen ? "" : "rotate-180")}
					/>
				</span>
			</button>

			{/* Dropdown menu - appears above */}
			{isOpen && (
				<div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-72 bg-bg-primary border border-border-secondary rounded-md shadow-lg overflow-hidden z-popover">
					{/* Header */}
					<div className="px-4 py-3 border-b border-border-secondary bg-bg-secondary">
						<p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
							{t("hub.profile_selector.title")}
						</p>
					</div>

					{/* Options */}
					<div className="max-h-64 overflow-auto">
						{profiles.map((profile) => {
							const isSelected = profile.uuid === selectedProfile.uuid;
							const Icon = profile.type === "company" ? Building2 : User;

							return (
								<button
									key={profile.uuid}
									type="button"
									onClick={() => handleSelect(profile.uuid)}
									className={cn(
										"w-full flex items-center p-4 hover:bg-hover-light transition-colors",
										isSelected && "bg-bg-secondary",
									)}
								>
									{/* Icon */}
									<div className="w-10 h-10 rounded-lg bg-bg-tertiary flex items-center justify-center flex-shrink-0">
										<Icon size={18} className="text-text-secondary" />
									</div>

									{/* Name and type */}
									<div className="ml-3 flex-1 min-w-0 text-left">
										<p
											className={cn(
												"text-sm truncate",
												isSelected
													? "font-medium text-text-primary"
													: "text-text-primary",
											)}
										>
											{profile.display_name}
										</p>
										<p className="text-xs text-text-secondary">
											{getTypeLabel(profile.type)}
										</p>
									</div>

									{/* Check mark */}
									{isSelected && (
										<Check
											size={18}
											className="text-success flex-shrink-0 ml-2"
										/>
									)}
								</button>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};

/**
 * Bottom navigation component for mobile devices.
 * Shows main navigation items plus profile switcher (when multiple profiles).
 * Only visible on mobile (< 640px).
 */
export const HubBottomNav = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const { hasMultipleProfiles } = useHubProfile();

	const isActive = (path: string) => {
		if (path === ROUTES.HUB.DASHBOARD) {
			return (
				location.pathname === ROUTES.HUB.ROOT ||
				location.pathname === ROUTES.HUB.DASHBOARD
			);
		}
		return location.pathname.startsWith(path);
	};

	return (
		<nav
			className="fixed bottom-0 inset-x-0 z-popover bg-bg-primary border-t border-border-secondary sm:hidden pb-[env(safe-area-inset-bottom,0px)]"
			aria-label={t(
				"hub.accessibility.main_navigation",
				"Navegación principal",
			)}
		>
			<div
				className={cn(
					"flex items-center justify-center h-16",
					hasMultipleProfiles ? "gap-8" : "gap-16",
				)}
			>
				{/* Dashboard */}
				<NavLink
					to={navItems[0].path}
					aria-current={isActive(navItems[0].path) ? "page" : undefined}
					aria-label={t(navItems[0].labelKey) || navItems[0].fallback}
					className={cn(
						"flex flex-col items-center justify-center h-full px-4 py-1 transition-colors",
						isActive(navItems[0].path)
							? "text-text-primary"
							: "text-text-secondary hover:text-text-primary",
					)}
				>
					<span
						aria-hidden="true"
						className={
							isActive(navItems[0].path)
								? "text-text-primary"
								: "text-text-tertiary"
						}
					>
						{navItems[0].icon}
					</span>
					<span className="mt-1 text-2xs font-medium truncate">
						{t(navItems[0].labelKey) || navItems[0].fallback}
					</span>
				</NavLink>

				{/* Profile */}
				<NavLink
					to={navItems[1].path}
					aria-current={isActive(navItems[1].path) ? "page" : undefined}
					aria-label={t(navItems[1].labelKey) || navItems[1].fallback}
					className={cn(
						"flex flex-col items-center justify-center h-full px-4 py-1 transition-colors",
						isActive(navItems[1].path)
							? "text-text-primary"
							: "text-text-secondary hover:text-text-primary",
					)}
				>
					<span
						aria-hidden="true"
						className={
							isActive(navItems[1].path)
								? "text-text-primary"
								: "text-text-tertiary"
						}
					>
						{navItems[1].icon}
					</span>
					<span className="mt-1 text-2xs font-medium truncate">
						{t(navItems[1].labelKey) || navItems[1].fallback}
					</span>
				</NavLink>

				{/* Profile Switcher - only when multiple profiles (right side) */}
				<ProfileSwitcherButton />
			</div>
		</nav>
	);
};

export default HubBottomNav;
